import { useMutation, useQuery } from '@apollo/client';
import { browserLogger } from '@newfront-insurance/dd-rum';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { NovaSettingsData, NovaSettingField } from '../../../hooks/use-nova-settings';
import type { ChatAppType } from '../../../queries/chats';
import {
  DISCONNECT_NOVA_MICROSOFT_GRAPH_CONNECTION,
  GET_NOVA_MICROSOFT_GRAPH_CONNECTION_STATUS,
  START_NOVA_MICROSOFT_GRAPH_AUTHORIZATION,
} from '../../../queries/mutations';
import {
  clearStoredMicrosoftGraphAuthorizationResult,
  clearStoredMicrosoftGraphAuthorizationState,
  NOVA_MICROSOFT_GRAPH_AUTHORIZATION_MESSAGE_TYPE,
  NOVA_MICROSOFT_GRAPH_AUTHORIZATION_POPUP_FEATURES,
  NOVA_MICROSOFT_GRAPH_AUTHORIZATION_RESULT_KEY,
  writeStoredMicrosoftGraphAuthorizationState,
} from '../lib/microsoft-graph-authorization';

export interface OutlookConnectController {
  // State
  hasSavedOutlookConnection: boolean;
  outlookConnectEnabled: boolean;

  /** True when Outlook is enabled but auth has expired and interactive login is required. */
  outlookNeedsReauth: boolean;

  // Actions
  setOutlookConnectEnabled(enabled: boolean): void;
  disconnectOutlook(): Promise<void>;

  /**
   * Attempt interactive login if Outlook is enabled but auth has expired.
   * Must be called from a user gesture (click handler) so the popup is not blocked.
   * Returns true if a login popup was triggered.
   */
  reconnectIfNeeded(): Promise<boolean>;
}

export function useOutlookConnectController({
  appType,
  canConnectOutlook,
  notify,
  serverSettings,
  onPersist,
}: {
  appType?: ChatAppType;
  canConnectOutlook: boolean;
  notify: { success: (msg: string) => void; error: (msg: string) => void };
  serverSettings?: NovaSettingsData | null;
  onPersist?: (field: NovaSettingField, value: unknown) => void;
}): OutlookConnectController {
  const activationHandledRef = useRef(false);
  const activationVersionRef = useRef(0);
  const isOutlookAddin = appType === 'OUTLOOK';
  const [startNovaMicrosoftGraphAuthorization] = useMutation(START_NOVA_MICROSOFT_GRAPH_AUTHORIZATION);
  const [disconnectNovaMicrosoftGraphConnection] = useMutation(DISCONNECT_NOVA_MICROSOFT_GRAPH_CONNECTION);

  // Initialize from server settings if available, default to false
  const [outlookConnectEnabled, setOutlookConnectEnabledState] = useState(
    () => canConnectOutlook && (serverSettings?.outlookConnectEnabled ?? false),
  );
  const [outlookNeedsReauth, setOutlookNeedsReauth] = useState(false);
  const {
    data: delegatedConnectionStatusData,
    refetch: refetchDelegatedConnectionStatus,
    loading: connectionLoading,
  } = useQuery(GET_NOVA_MICROSOFT_GRAPH_CONNECTION_STATUS, {
    fetchPolicy: 'cache-and-network',
    // Connection health is available even when the shared access toggle is off.
    skip: !canConnectOutlook,
  });
  const delegatedConnectionStatus = delegatedConnectionStatusData?.getNovaMicrosoftGraphConnectionStatus;

  const hasSavedOutlookConnection =
    canConnectOutlook && Boolean(delegatedConnectionStatus?.connectionUuid || delegatedConnectionStatus?.connected);

  // Sync from server settings when they arrive
  useEffect(() => {
    if (serverSettings != null) {
      setOutlookConnectEnabledState(canConnectOutlook && serverSettings.outlookConnectEnabled);
    }
  }, [canConnectOutlook, serverSettings]);

  useEffect(() => {
    setOutlookNeedsReauth(
      canConnectOutlook &&
        !connectionLoading &&
        (isOutlookAddin || outlookConnectEnabled) &&
        (delegatedConnectionStatus?.needsReauth ?? false),
    );
  }, [
    canConnectOutlook,
    connectionLoading,
    isOutlookAddin,
    delegatedConnectionStatus?.needsReauth,
    outlookConnectEnabled,
  ]);

  // Users who cannot connect Outlook (e.g. WTW colleagues) must never have it on:
  // reset local state and persist the disable when a stale "on" setting arrives.
  useEffect(() => {
    if (canConnectOutlook) {
      return;
    }

    setOutlookNeedsReauth(false);

    if (outlookConnectEnabled) {
      setOutlookConnectEnabledState(false);
      onPersist?.('outlookConnectEnabled', false);
    }
  }, [canConnectOutlook, outlookConnectEnabled, onPersist]);

  const delegatedAuthorizationCleanupRef = useRef<(() => void) | null>(null);

  useEffect(
    () => () => {
      delegatedAuthorizationCleanupRef.current?.();
    },
    [],
  );

  // Disable Outlook and reset state
  const disableOutlook = useCallback(() => {
    activationHandledRef.current = true;
    activationVersionRef.current += 1;
    setOutlookConnectEnabledState(false);
    setOutlookNeedsReauth(false);
    onPersist?.('outlookConnectEnabled', false);

    // If the user turns the toggle off while an OAuth popup is still in
    // flight, stop listening for that stale callback before disabling the
    // connection. Otherwise a late callback can update the UI after the user
    // explicitly disabled Outlook Connect.
    delegatedAuthorizationCleanupRef.current?.();
    clearStoredMicrosoftGraphAuthorizationResult();
    clearStoredMicrosoftGraphAuthorizationState();
  }, [onPersist]);

  const disconnectOutlook = useCallback(async () => {
    await disconnectNovaMicrosoftGraphConnection();
    disableOutlook();
    await refetchDelegatedConnectionStatus();
  }, [disconnectNovaMicrosoftGraphConnection, disableOutlook, refetchDelegatedConnectionStatus]);

  const enableSharedAccess = useCallback(() => {
    setOutlookConnectEnabledState(true);
    setOutlookNeedsReauth(false);
    onPersist?.('outlookConnectEnabled', true);
  }, [onPersist]);

  // An entry can enable a ready connection once. Subsequent settings changes
  // (including disabling from another client) must not enable it again.
  useEffect(() => {
    if (
      !isOutlookAddin ||
      !canConnectOutlook ||
      connectionLoading ||
      !serverSettings ||
      !delegatedConnectionStatus ||
      activationHandledRef.current
    )
      return;
    activationHandledRef.current = true;
    if (delegatedConnectionStatus.connected && !serverSettings.outlookConnectEnabled) enableSharedAccess();
  }, [
    isOutlookAddin,
    canConnectOutlook,
    connectionLoading,
    serverSettings,
    delegatedConnectionStatus,
    enableSharedAccess,
  ]);

  useEffect(() => {
    if (!isOutlookAddin || !canConnectOutlook) return;
    let away = false;
    let disposed = false;
    const leave = () => {
      away = true;
      activationVersionRef.current += 1;
    };
    const enter = () => {
      if (!away || document.hidden) return;
      away = false;
      // Closing the authorization popup is handled by its completion callback.
      if (delegatedAuthorizationCleanupRef.current) return;
      activationHandledRef.current = true;
      const version = ++activationVersionRef.current;
      void refetchDelegatedConnectionStatus()
        .then(({ data }) => {
          if (
            !disposed &&
            version === activationVersionRef.current &&
            data?.getNovaMicrosoftGraphConnectionStatus?.connected
          ) {
            enableSharedAccess();
          }
        })
        .catch((error) => browserLogger.error('Could not check Outlook connection on add-in entry', { error }));
    };
    const visibilityChanged = () => {
      if (document.hidden) leave();
      else enter();
    };
    window.addEventListener('blur', leave);
    window.addEventListener('focus', enter);
    document.addEventListener('visibilitychange', visibilityChanged);
    return () => {
      disposed = true;
      window.removeEventListener('blur', leave);
      window.removeEventListener('focus', enter);
      document.removeEventListener('visibilitychange', visibilityChanged);
    };
  }, [isOutlookAddin, canConnectOutlook, refetchDelegatedConnectionStatus, enableSharedAccess]);

  // Enable Outlook and trigger authentication
  const enableOutlook = useCallback(async () => {
    if (!canConnectOutlook) {
      return;
    }

    activationHandledRef.current = true;
    if (delegatedConnectionStatus?.connected) {
      enableSharedAccess();
      return;
    }

    // The callback page communicates completion through localStorage because
    // popup postMessage can be missed if the opener reloads or the popup is
    // closed immediately after returning from Microsoft. Clear any stale
    // result before starting so a previous failed attempt cannot disconnect
    // the fresh pending connection.
    clearStoredMicrosoftGraphAuthorizationResult();
    clearStoredMicrosoftGraphAuthorizationState();

    // Open a blank popup synchronously from the user's click. Waiting until
    // after the start mutation returns would make browsers treat it as a
    // non-user-initiated popup and block it.
    const popup = window.open(
      '',
      'nova-microsoft-graph-authorization',
      NOVA_MICROSOFT_GRAPH_AUTHORIZATION_POPUP_FEATURES,
    );

    if (!popup) {
      disableOutlook();
      notify.error('Outlook authentication popup was blocked. Please allow popups and try again.');
      return;
    }

    try {
      let completed = false;
      let loggedPopupClosed = false;
      const authorizationTimeoutRef: { current?: number } = {};
      let cleanup: () => void = () => {};
      const completeAuthorization = (status: 'success' | 'error') => {
        // Completion can arrive through both postMessage and localStorage
        // polling. Keep the handler idempotent so a successful callback does
        // not double-toast or race with cleanup.
        if (completed) {
          return;
        }

        completed = true;
        cleanup();

        if (status === 'success') {
          enableSharedAccess();
          void refetchDelegatedConnectionStatus();
          notify.success('Outlook connected successfully');
        } else {
          disableOutlook();
          notify.error('Outlook authentication failed: Please try again.');
        }
      };
      const pollInterval = window.setInterval(() => {
        const storedResult = localStorage.getItem(NOVA_MICROSOFT_GRAPH_AUTHORIZATION_RESULT_KEY);
        if (storedResult) {
          localStorage.removeItem(NOVA_MICROSOFT_GRAPH_AUTHORIZATION_RESULT_KEY);
          try {
            completeAuthorization(JSON.parse(storedResult).status === 'success' ? 'success' : 'error');
          } catch {
            completeAuthorization('error');
          }
          return;
        }

        // Some browsers report popup.closed unreliably once the popup has
        // navigated to login.microsoftonline.com. Treat the callback result
        // as the source of truth; using popup.closed as cancellation can
        // disconnect the backend connection while the user is still signing in.
        if (!completed && popup.closed && !loggedPopupClosed) {
          loggedPopupClosed = true;
          browserLogger.info('[OutlookConnect] Delegated MS Graph authorization popup appears closed');
        }
      }, 500);
      const handleAuthorizationMessage = (event: MessageEvent) => {
        if (
          event.origin !== window.location.origin ||
          event.data?.type !== NOVA_MICROSOFT_GRAPH_AUTHORIZATION_MESSAGE_TYPE
        ) {
          return;
        }

        completeAuthorization(event.data.status === 'success' ? 'success' : 'error');
      };
      const handleAuthorizationStorage = (event: StorageEvent) => {
        if (event.key !== NOVA_MICROSOFT_GRAPH_AUTHORIZATION_RESULT_KEY || !event.newValue) {
          return;
        }

        localStorage.removeItem(NOVA_MICROSOFT_GRAPH_AUTHORIZATION_RESULT_KEY);
        try {
          completeAuthorization(JSON.parse(event.newValue).status === 'success' ? 'success' : 'error');
        } catch {
          completeAuthorization('error');
        }
      };
      cleanup = () => {
        window.clearInterval(pollInterval);
        if (authorizationTimeoutRef.current != null) {
          window.clearTimeout(authorizationTimeoutRef.current);
        }
        window.removeEventListener('message', handleAuthorizationMessage);
        window.removeEventListener('storage', handleAuthorizationStorage);
        delegatedAuthorizationCleanupRef.current = null;
      };

      delegatedAuthorizationCleanupRef.current?.();
      delegatedAuthorizationCleanupRef.current = cleanup;
      window.addEventListener('message', handleAuthorizationMessage);
      window.addEventListener('storage', handleAuthorizationStorage);

      browserLogger.info('[OutlookConnect] Starting delegated MS Graph authorization');
      const { data } = await startNovaMicrosoftGraphAuthorization({
        variables: {
          command: {
            // users-service stores this exact redirect URI with the pending
            // connection and validates it during callback completion. Build
            // it from the current origin so local, stage, and prod each use
            // the host that initiated the popup.
            redirectUri: `${window.location.origin}${appType === 'OUTLOOK' ? '/nova-addins' : ''}/outlook-connections/oauth/callback`,
          },
        },
      });
      const authorization = data?.startNovaMicrosoftGraphAuthorization;

      if (!authorization?.authorizationUrl) {
        throw new Error('Unable to start Microsoft authorization');
      }

      // If the user abandons the popup and no callback ever arrives, fail the
      // optimistic toggle once the backend's pending authorization state has
      // expired. This avoids relying on popup.closed while still preventing an
      // indefinitely enabled UI with no usable connection.
      const authorizationExpiresAt = new Date(authorization.expiresAt).getTime();
      const authorizationTimeoutMs = Math.max(0, authorizationExpiresAt - Date.now()) + 5000;
      authorizationTimeoutRef.current = window.setTimeout(() => {
        completeAuthorization('error');
      }, authorizationTimeoutMs);

      writeStoredMicrosoftGraphAuthorizationState({
        connectionUuid: authorization.connectionUuid,
        expiresAt: authorization.expiresAt,
        // The backend-generated state is checked both here and by
        // users-service. Keeping it in browser storage lets the callback
        // page reject stray or replayed Microsoft redirects.
        state: authorization.state,
      });

      browserLogger.info('[OutlookConnect] Delegated MS Graph authorization started', {
        connectionUuid: authorization.connectionUuid,
        expiresAt: authorization.expiresAt,
      });
      // eslint-disable-next-line no-restricted-syntax -- Navigates a separately opened popup, never the embed or host window.
      popup.location.assign(authorization.authorizationUrl);
    } catch (error) {
      browserLogger.error('[OutlookConnect] Failed to start delegated MS Graph authorization', { error });
      delegatedAuthorizationCleanupRef.current?.();
      popup.close();
      disableOutlook();
      notify.error('Outlook authentication failed: Please try again.');
    }
  }, [
    appType,
    enableSharedAccess,
    delegatedConnectionStatus?.connected,
    canConnectOutlook,
    startNovaMicrosoftGraphAuthorization,
    refetchDelegatedConnectionStatus,
    disableOutlook,
    notify,
  ]);

  // Handle Outlook Connect toggle changes
  const setOutlookConnectEnabled = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        enableOutlook();
      } else {
        disableOutlook();
      }
    },
    [enableOutlook, disableOutlook],
  );

  // Restart the delegated authorization flow if Outlook is enabled but auth has expired.
  // Called from click handlers (user gesture) so the popup is allowed by the browser.
  const reconnectIfNeeded = useCallback(async (): Promise<boolean> => {
    if (!canConnectOutlook || (!isOutlookAddin && !outlookConnectEnabled) || !outlookNeedsReauth) return false;

    await enableOutlook();
    return true;
  }, [canConnectOutlook, isOutlookAddin, outlookConnectEnabled, outlookNeedsReauth, enableOutlook]);

  return useMemo(
    () => ({
      hasSavedOutlookConnection,
      outlookConnectEnabled,
      outlookNeedsReauth,
      disconnectOutlook,
      setOutlookConnectEnabled,
      reconnectIfNeeded,
    }),
    [
      hasSavedOutlookConnection,
      outlookConnectEnabled,
      outlookNeedsReauth,
      disconnectOutlook,
      setOutlookConnectEnabled,
      reconnectIfNeeded,
    ],
  );
}
